import _ from 'lodash';
import debounce from 'debounce-promise';
import axios from 'axios';
import striptags from 'striptags';
import de from 'vee-validate/dist/locale/de';
import en from 'vee-validate/dist/locale/en';
import { Validator } from 'vee-validate';

var API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT;

import isIP from './isIP';
import isFQDN from './isFQDN';

import PropertyApi from '@/extensions/shop/store/api/property';
import CustomerApi from '@/extensions/shop/store/api/customer';
import PropSetApi from '@/extensions/shop/store/api/propSet';
import ProductApi from '@/extensions/shop/store/api/product';
import ArticleApi from '@/extensions/shop/store/api/article';
import CouponApi from '@/extensions/shop/store/api/coupon';
import CampaignApi from '@/extensions/marketing/store/api/campaign';
import MemberApi from '@/extensions/restrictedArea/store/api/member';
import PageApi from '@/store/api/cms/page';
import WizardApi from '@/store/api/cms/wizard';

const validations = {};
const options = {};

// TODO
// 1. get rid of vee-element
// 2. update vee-validate
// 3. refactor all forms
// see: https://logaretm.github.io/vee-validate/guide/3rd-party-libraries.html#element-ui

// Register available locales
Validator.localize('de', de);
Validator.localize('en', en);

// Set default locale to German (matches setup store default)
Validator.localize('de');

// Custom validator messages (localized)
Validator.localize('de', {
    messages: {
        html_max: (field, [length]) => `${field} darf nicht länger als ${length} Zeichen sein`,
        password_strength: 'Das Passwort ist zu schwach.',
        // Format validators
        web_address: 'Dies ist keine gültige Domain / IP-Adresse.',
        absolute_path: 'Dies ist kein gültiger Pfad.',
        sku: 'Dies ist keine gültige Artikelnummer.',
        key: 'Dies ist kein gültiger Schlüssel.',
        picked: (field, [min, max]) => max
            ? `Das Feld {field} erfordert zwischen ${min} und ${max} Einträge.`
            : `Das Feld {field} erfordert mindestens ${min} ${min > 1 ? 'Einträge' : 'Eintrag'}.`,

        // Uniqueness validators
        unique_subdomain: 'Diese Subdomain ist bereits vergeben.',
        unique_user_email: 'Diese E-Mail-Adresse ist bereits vergeben.',
        unique_product_nr: 'Dieser Wert ist bereits vergeben.',
        unique_product_slug: 'Dieser URL-Schlüssel ist bereits vergeben.',
        unique_member_email: 'Diese E-Mail-Adresse ist bereits vergeben.',
        unique_customer_nr: 'Diese Kundennummer ist bereits vergeben.',
        unique_property_key: 'Dieser Schlüssel ist bereits vergeben.',
        unique_prop_set_key: 'Dieser Schlüssel ist bereits vergeben.',
        unique_article_sku: 'Diese Artikelnummer ist bereits vergeben.',
        unique_category_page_key: 'Dieser Schlüssel ist bereits vergeben.',
        unique_coupon_code: 'Dieser Gutscheincode ist bereits vergeben.',
        unique_marketing_campaign_key: 'Dieser Kampagnenschlüssel ist bereits vergeben.'
    }
});
Validator.localize('en', {
    messages: {
        html_max: (field, [length]) => `The ${field} field may not be greater than ${length} characters`,
        // Format validators
        web_address: 'This is not a valid domain name / IP address.',
        absolute_path: 'This is not a valid path.',
        sku: 'This is not a valid SKU.',
        key: 'This is not a valid key.',
        picked: (field, [min, max]) => max
            ? `The {field} field requires between ${min} and ${max} items.`
            : `The {field} field requires at least ${min} item${min > 1 ? 's' : ''}.`,

        password_strength: 'The password is too weak.',
        // Uniqueness validators
        unique_subdomain: 'This subdomain is already taken.',
        unique_user_email: 'This email address is already taken.',
        unique_product_nr: 'This value is already taken.',
        unique_product_slug: 'This URL key is already taken.',
        unique_member_email: 'This email address is already taken.',
        unique_customer_nr: 'This customer number is already taken.',
        unique_property_key: 'This key is already taken.',
        unique_prop_set_key: 'This key is already taken.',
        unique_article_sku: 'This SKU is already taken.',
        unique_category_page_key: 'This key is already taken.',
        unique_coupon_code: 'This coupon code is already taken.',
        unique_marketing_campaign_key: 'This campaign key is already taken.'
    }
});

export function switchValidatorLocale(locale) {
    Validator.localize(locale);
}

export default new Validator(validations, options);

Validator.extend('picked', {
    validate(data, args) {
        const min = Number(args[0]);
        const max = Number(args[1]);
        return args.length === 1 ? data.length >= min : data.length >= min && data.length <= max;
    }
    // getMessage is not defined - vee-validate will use the localized messages
});

// Like the built-in `max:n`, but counts visible characters rather than the
// raw string length. Use this on rich-text editor fields (Quill etc.) where
// the model holds HTML and the user-visible content is what should be
// constrained.
Validator.extend('html_max', {
    validate(value, [length]) {
        const stripped = striptags(value || '');
        return stripped.length <= Number(length);
    }
});

Validator.extend('web_address', {
    validate: value => isIP(value, '') || isFQDN(value)
});

// Validator.extend('no_protocol', {
//     validate: value => {
//         return {
//             valid: ! value.match(/^(ftp:\/\/|http:\/\/|https:\/\/)/i),
//             data: {
//                 message: 'Protocol is not allowed.'
//             }
//         }
//     },
//     getMessage: (field, params, data) => {
//         return data.message;
//     }
// })
//
// Validator.extend('domain', {
//     validate: value => {
//         return {
//             valid: value.match(/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i),
//             data: {
//                 message: 'This is not a valid domain.'
//             }
//         }
//     },
//     getMessage: (field, params, data) => {
//         return data.message;
//     }
// })

Validator.extend('absolute_path', {
    validate: value => !!value.match(/^\/[-A-Z0-9_]+$/im)
});

Validator.extend('unique_product_nr', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            product: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return ProductApi.validateNR(value, data.shop, data.product);
    }, 300)
});

Validator.extend('unique_product_slug', {
    validate: debounce(function (value, rule) {
        var data = {
            shop: null,
            product: null
        };

        if (rule.length) {
            rule.forEach(function (entry) {
                var result = JSON.parse('{' + entry + '}');
                data = Object.assign({}, data, result);
            });
        }

        return ProductApi.validateSlug(value, data.shop, data.product);
    }, 300)
});

Validator.extend('unique_member_email', {
    validate: debounce((value, rule) => {
        let data = {
            cms: null,
            member: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return MemberApi.validateEmail(value, data.cms, data.member);
    }, 300)
});

Validator.extend('unique_customer_nr', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            customer: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return CustomerApi.validateNR(value, data.shop, data.customer);
    }, 300)
});

Validator.extend('unique_property_key', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            property: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return PropertyApi.validateKey(value, data.shop, data.property);
    }, 300)
});

Validator.extend('unique_property_value_option_key', {
    validate: debounce((value, rule) => {
        let data = {
            property: null,
            valueOption: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        if (!data.property) return true;

        return PropertyApi.validateValueOptionKey(value, data.property, data.valueOption);
    }, 300)
});

Validator.extend('unique_prop_set_key', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            propSet: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return PropSetApi.validateKey(value, data.shop, data.propSet);
    }, 300)
});

Validator.extend('sku', {
    validate: value => !!value.match(/^[-A-Z0-9_#.$%+/]+$/im)
});

Validator.extend('unique_article_sku', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            excludedArticles: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                if (entry.indexOf('excludedArticles') >= 0) {
                    const parts = _.split(entry, ':');
                    data.excludedArticles = _.split(parts[1], '+');
                } else {
                    const result = JSON.parse(`{${entry}}`);
                    data = { ...data, ...result };
                }
            });
        }

        return ArticleApi.validateSKU(value, data.shop, data.excludedArticles);
    }, 300)
});

Validator.extend('key', {
    validate: value => !!value.match(/^[A-Z0-9_]+[-A-Z0-9_]*$/im)
});

Validator.extend('unique_category_page_key', {
    validate: debounce((value, rule) => {
        let data = {
            category: null,
            page: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return PageApi.validateKey(value, data.category, data.page);
    }, 300)
});

Validator.extend('unique_coupon_code', {
    validate: debounce((value, rule) => {
        let data = {
            shop: null,
            coupon: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return CouponApi.validateCode(value, data.shop, data.coupon);
    }, 300)
});

Validator.extend('unique_marketing_campaign_key', {
    validate: debounce((value, rule) => {
        let data = {
            cms: null,
            campaign: null
        };

        if (rule.length) {
            rule.forEach(entry => {
                const result = JSON.parse(`{${entry}}`);
                data = { ...data, ...result };
            });
        }

        return CampaignApi.validateKey(value, data.cms, data.campaign);
    }, 300)
});

Validator.extend('unique_subdomain', {
    validate: debounce((value) => {
        // Length check is handled by min:3 rule, so we just check availability
        if (!value || value.length < 3) {
            return true; // Let min:3 handle this validation
        }

        return WizardApi.checkSubdomainAvailability(value);
    }, 500)
    // getMessage is not defined - vee-validate will use the localized messages from Validator.localize()
});

Validator.extend('unique_user_email', {
    validate: debounce((value) => {
        if (!value || value.length < 3) {
            return true; // Let email rule handle format validation
        }

        return WizardApi.checkUserEmailAvailability(value);
    }, 500)
});

Validator.extend('password_strength', {
    validate: debounce((value) => {
        if (!value) return false;
        return axios.post(API_ENDPOINT + 'zxcvbn', { password: value })
            .then(function(response) {
                var score = _.get(response, 'data.score', 0);
                return score >= 3;
            })
            .catch(function() {
                return false;
            });
    }, 300)
});
